import apiClient from './client';

export interface ExternalSource {
  id: string;
  series_id: string;
  source_type: string;
  url: string;
  manga_hash_id: string | null;
  manga_title: string | null;
  preferred_group_id: number | null;
  preferred_group_name: string | null;
  chapters_cache: {
    chapters: ComixChapter[];
    groups: ComixGroup[];
  } | null;
  chapters_cache_updated_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ComixChapter {
  chapter_id: number;
  manga_id: number;
  number: number;
  volume: number;
  language: string | null;
  scanlation_group_name: string | null;
  scanlation_group_id: number | null;
  votes: number | null;
  created_at: number | null;
  is_new?: boolean;
}

export interface ComixGroup {
  scanlation_group_id: number;
  name: string;
}

export interface RefreshResponse {
  manga: {
    manga_id: number;
    hash_id: string;
    title: string;
    slug: string | null;
  };
  chapters: ComixChapter[];
  groups: ComixGroup[];
  cached_at: string;
}

export interface DownloadBatch {
  id: string;
  series_id: string;
  source_id: string;
  status: 'pending' | 'downloading' | 'translating' | 'completed' | 'download_failed' | 'translation_failed' | 'cancelled';
  chapters_requested: ComixChapter[];
  chapters_progress: Record<string, {
    chapter_no: number;
    download_status: string;
    zip_path?: string;
    job_id?: string;
    translation_status: string;
    step?: string;
    progress?: number;
    current_page?: number;
    total_pages?: number;
    error?: string;
  }>;
  current_chapter_no: number | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export const externalSourcesApi = {
  list: async (seriesId: string): Promise<ExternalSource[]> => {
    const { data } = await apiClient.get(`/series/${seriesId}/external-sources`);
    return data;
  },

  create: async (seriesId: string, url: string, sourceType = 'comix.to'): Promise<ExternalSource> => {
    const { data } = await apiClient.post(`/series/${seriesId}/external-sources`, {
      url,
      source_type: sourceType,
    });
    return data;
  },

  update: async (
    seriesId: string,
    sourceId: string,
    patch: { preferred_group_id?: number; preferred_group_name?: string; url?: string }
  ): Promise<ExternalSource> => {
    const { data } = await apiClient.patch(`/series/${seriesId}/external-sources/${sourceId}`, patch);
    return data;
  },

  delete: async (seriesId: string, sourceId: string): Promise<void> => {
    await apiClient.delete(`/series/${seriesId}/external-sources/${sourceId}`);
  },

  refresh: async (seriesId: string, sourceId: string): Promise<RefreshResponse> => {
    const { data } = await apiClient.post(`/series/${seriesId}/external-sources/${sourceId}/refresh`);
    return data;
  },

  download: async (seriesId: string, sourceId: string, chapterIds: number[]): Promise<{
    batch_id: string;
    queue_job_id: string;
    chapters_count: number;
  }> => {
    const { data } = await apiClient.post(`/series/${seriesId}/external-sources/${sourceId}/download`, {
      chapter_ids: chapterIds,
    });
    return data;
  },

  listBatches: async (seriesId: string): Promise<DownloadBatch[]> => {
    const { data } = await apiClient.get(`/series/${seriesId}/download-batches`);
    return data;
  },

  getBatch: async (seriesId: string, batchId: string): Promise<DownloadBatch> => {
    const { data } = await apiClient.get(`/series/${seriesId}/download-batches/${batchId}`);
    return data;
  },

  cancelBatch: async (seriesId: string, batchId: string): Promise<void> => {
    await apiClient.post(`/series/${seriesId}/download-batches/${batchId}/cancel`);
  },
};
